import { useTheme } from 'utils'
import Circle from './Circle'
import Flex, { type Props as FlexProps } from './Flex'

export interface Props extends FlexProps {
  variant?: 'default' | 'hero'
}

export default function Backdrop({ children, variant = 'default', ...rest }: Props) {
  const { theme } = useTheme()

  if (variant === 'hero') {
    return (
      <Flex
        overflow="hidden"
        minHeight="100%"
        flex={1}
        position="relative"
        bgImage="linear-gradient(135deg, #7C316D 0%, #0B0B3C 30%, #1A2E73 60%, #0B0B3C 100%)"
        bgSize="200% 200%"
        animation="bgShift 16s ease-in-out infinite"
      >
        <Flex
          position="absolute"
          inset={0}
          pointerEvents="none"
          zIndex={0}
          overflow="hidden"
        >
          <Flex
            position="absolute"
            w="600px"
            h="600px"
            borderRadius="full"
            opacity={0.07}
            filter="blur(120px)"
            top="10%"
            left="60%"
            bg="radial-gradient(circle, #0CF4DF, transparent 70%)"
            animation="ambientFloat1 12s ease-in-out infinite"
          />
          <Flex
            position="absolute"
            w="500px"
            h="500px"
            borderRadius="full"
            opacity={0.05}
            filter="blur(100px)"
            bottom="0%"
            left="10%"
            bg="radial-gradient(circle, #7C316D, transparent 70%)"
            animation="ambientFloat2 15s ease-in-out infinite"
          />
          <Flex
            position="absolute"
            w="400px"
            h="400px"
            borderRadius="full"
            opacity={0.04}
            filter="blur(80px)"
            top="40%"
            right="5%"
            bg="radial-gradient(circle, #254FE5, transparent 70%)"
            animation="ambientFloat3 18s ease-in-out infinite"
          />
        </Flex>
        <Flex
          position="absolute"
          inset={0}
          pointerEvents="none"
          zIndex={1}
          overflow="hidden"
          animation="arcDrift 20s ease-in-out infinite"
        >
          <svg
            viewBox="0 0 600 500"
            fill="none"
            style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              top: 0,
              right: 0,
              objectFit: 'cover',
              objectPosition: 'right bottom',
            }}
            preserveAspectRatio="xMaxYMax meet"
          >
            <path
              d="M 600 300 A 200 200 0 0 0 400 500"
              stroke="white"
              strokeOpacity="0.08"
              strokeWidth="1.4"
              fill="none"
            />
            <path
              d="M 600 150 A 350 350 0 0 0 250 500"
              stroke="white"
              strokeOpacity="0.05"
              strokeWidth="1"
              fill="none"
            />
            <path
              d="M 600 0 A 500 500 0 0 0 100 500"
              stroke="white"
              strokeOpacity="0.03"
              strokeWidth="0.8"
              fill="none"
            />
          </svg>
        </Flex>

        <Flex zIndex={2} {...rest}>
          {children}
        </Flex>
      </Flex>
    )
  }

  return (
    <Flex
      bg={
        theme === 'light'
          ? '#E9F0F5'
          : 'linear-gradient(308deg, #5B9C85 18%, #3E7F6D 25%, #1A2E73 88%)'
      }
      overflow="hidden"
      minHeight="100%"
      flex={1}
      position="relative"
    >
      <Flex position="absolute" inset={0} overflow="hidden">
        {theme === 'light' && (
          <Circle
            position="absolute"
            top="0vh"
            left="0"
            size="50vh"
            bg="#FFE2C2"
            filter="blur(170px)"
          />
        )}
        <Circle
          position="absolute"
          bottom="5vh"
          size="35vh"
          bg="#E83A5A"
          filter={theme === 'light' ? 'blur(150px)' : 'blur(250px)'}
        />
        <Circle
          position="absolute"
          top="40vh"
          left="15vw"
          size="30vh"
          bg="#C9B7ED"
          filter={theme === 'light' ? 'blur(130px)' : 'blur(220px)'}
        />

        <Circle
          position="absolute"
          top="15vh"
          right="0px"
          size="30vh"
          bg="#3C4BD3"
          filter={theme === 'light' ? 'blur(200px)' : 'blur(150px)'}
        />
      </Flex>

      <Flex zIndex={1} {...rest}>
        {children}
      </Flex>
    </Flex>
  )
}
